import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { EMERGENCY_PHONE_NOTICE, StudentOnboardingInput } from '@mdt/contracts';
import { Alert, Button, Checkbox, ErrorAlert, Loading, TextField } from '../components/kit.tsx';
import { SafeLink } from '../components/SafeLink.tsx';
import { ApiError } from '../lib/api.ts';
import { usePageTitle } from '../lib/format.ts';
import { useRouter } from '../lib/router.tsx';
import { useSession } from '../lib/session.tsx';

/**
 * First sign-in of a student (ADR-14): Student ID, names, emergency phone with the PDPA notice. This form
 * holds personal data, so it has no draft autosave. It runs on the onboarding ticket cookie, not a session.
 */
type Values = { studentId: string; nameTh: string; nameEn: string; emergencyPhone: string; noticeAccepted: boolean };
type Outcome = { outcome: 'created' | 'pending_review' | 'rejected'; reason?: string };

const FIELD_ERRORS: Record<keyof Values, string> = {
  studentId: 'onboarding.errors.studentId',
  nameTh: 'onboarding.errors.nameTh',
  nameEn: 'onboarding.errors.nameEn',
  emergencyPhone: 'onboarding.errors.emergencyPhone',
  noticeAccepted: 'onboarding.errors.notice',
};

async function post(path: string, body: unknown) {
  const res = await fetch(path, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  const json = (await res.json().catch(() => ({}))) as { ok?: boolean; result?: Outcome; error?: { code: string; messageKey: string } };
  if (!res.ok && res.status !== 202) throw new ApiError(json.error?.code ?? 'INTERNAL', json.error?.messageKey ?? 'err.internal', res.status);
  return json.result;
}

export function Onboarding() {
  const { t, i18n } = useTranslation();
  const { navigate } = useRouter();
  const { refresh } = useSession();
  usePageTitle(t('onboarding.title'));
  const [ticket, setTicket] = useState<'loading' | 'ok' | 'expired'>('loading');
  const [email, setEmail] = useState('');
  const [values, setValues] = useState<Values>({ studentId: '', nameTh: '', nameEn: '', emergencyPhone: '', noticeAccepted: false });
  const [errors, setErrors] = useState<Partial<Record<keyof Values, string>>>({});
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [done, setDone] = useState<Outcome | 'staff' | null>(null);
  const [focusError, setFocusError] = useState(0);

  // After a failed submit, focus goes to the first field in error (WCAG 3.3.1): its error is read out with it.
  useEffect(() => {
    if (focusError) document.querySelector<HTMLElement>('form [aria-invalid="true"]')?.focus();
  }, [focusError]);

  useEffect(() => {
    void fetch('/api/onboarding', { credentials: 'same-origin' }).then(async (r) => {
      if (!r.ok) return setTicket('expired');
      setEmail(((await r.json()) as { email: string }).email);
      setTicket('ok');
    }, () => setTicket('expired'));
  }, []);

  const set = <K extends keyof Values>(k: K, v: Values[K]) => setValues((prev) => ({ ...prev, [k]: v }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFailure(null);
    const parsed = StudentOnboardingInput.safeParse({ ...values, noticeVersion: EMERGENCY_PHONE_NOTICE.version, noticeAccepted: values.noticeAccepted || undefined });
    if (!parsed.success) {
      const next: Partial<Record<keyof Values, string>> = {};
      for (const issue of parsed.error.issues) {
        const k = issue.path[0] as keyof Values;
        if (k in FIELD_ERRORS) next[k] = t(FIELD_ERRORS[k]);
      }
      setErrors(next);
      setFocusError((n) => n + 1);   // focus moves after the errors are on screen (see the effect below)
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      const out = await post('/api/onboarding', parsed.data);
      if (out?.outcome === 'created') {
        await refresh();
        navigate('/', { replace: true });
        return;
      }
      setDone(out ?? { outcome: 'rejected' });
    } catch (err) {
      setFailure(err);
    } finally {
      setBusy(false);
    }
  }

  async function iAmStaff() {
    setBusy(true);
    try {
      await post('/api/onboarding/staff', {});
      setDone('staff');
    } catch (err) {
      setFailure(err);
    } finally {
      setBusy(false);
    }
  }

  if (ticket === 'loading') return <Loading />;
  if (ticket === 'expired' && !done) {
    return (
      <div className="stack">
        <h1>{t('onboarding.title')}</h1>
        <Alert tone="warning"><p>{t('err.onboarding.ticket_expired')}</p></Alert>
        <p><SafeLink href="/login" className="btn">{t('signedOut.signInAgain')}</SafeLink></p>
      </div>
    );
  }
  if (done) {
    const key = done === 'staff' ? 'auth.notProvisioned.staff'
      : done.reason === 'ROSTER_CLAIM' ? 'auth.notProvisioned.rosterClaim'
      : done.reason === 'COHORT_NOT_OPEN' ? 'auth.notProvisioned.cohortNotOpen'
      : done.reason === 'STUDENT_ID_TAKEN' ? 'err.identity.student_id_taken'
      : 'onboarding.signInAgain';
    return (
      <div className="stack">
        <h1>{t('auth.notProvisioned.title')}</h1>
        <Alert tone="info"><p>{t(key)}</p></Alert>
        <p><SafeLink href="/login">{t('signedOut.signInAgain')}</SafeLink></p>
      </div>
    );
  }

  const notice = i18n.language === 'th' ? EMERGENCY_PHONE_NOTICE.th : EMERGENCY_PHONE_NOTICE.en;
  return (
    <form className="stack" noValidate onSubmit={(e) => void submit(e)}>
      <h1>{t('onboarding.title')}</h1>
      <p>{t('onboarding.intro', { email })}</p>
      <ErrorAlert error={failure} />
      <TextField label={t('onboarding.studentId')} hint={t('onboarding.studentIdHint')} required inputMode="numeric" autoComplete="off"
        value={values.studentId} error={errors.studentId ?? null} onChange={(e) => set('studentId', e.currentTarget.value)} />
      <TextField label={t('onboarding.nameTh')} required lang="th" autoComplete="name"
        value={values.nameTh} error={errors.nameTh ?? null} onChange={(e) => set('nameTh', e.currentTarget.value)} />
      <TextField label={t('onboarding.nameEn')} required lang="en" autoComplete="name"
        value={values.nameEn} error={errors.nameEn ?? null} onChange={(e) => set('nameEn', e.currentTarget.value)} />
      <TextField label={t('onboarding.emergencyPhone')} hint={t('onboarding.emergencyPhoneHint')} required type="tel" inputMode="tel" autoComplete="tel"
        value={values.emergencyPhone} error={errors.emergencyPhone ?? null} onChange={(e) => set('emergencyPhone', e.currentTarget.value)} />
      <div className="notice" id="pdpa-notice" lang={i18n.language === 'th' ? 'th' : 'en'}>{notice}</div>
      <Checkbox label={t('onboarding.acceptNotice')} checked={values.noticeAccepted} describedBy="pdpa-notice"
        onChange={(v) => set('noticeAccepted', v)} />
      {errors.noticeAccepted ? <p className="field__error" role="alert">{errors.noticeAccepted}</p> : null}
      <div className="row">
        <Button type="submit" busy={busy}>{t('onboarding.submit')}</Button>
        <Button variant="secondary" disabled={busy} onClick={() => void iAmStaff()}>{t('onboarding.iAmStaff')}</Button>
      </div>
    </form>
  );
}
